import { supabase } from './supabase'
import type { SavedSearch } from '@/types/profile'

// Default cap for getSavedSearches — matches the 50/user retention limit
// enforced by cleanup_saved_searches() so a single-user fetch can never
// exceed one retention window of results.
export const DEFAULT_SAVED_SEARCH_LIMIT = 50

export async function getSavedSearches(
  userId: string,
  limit: number = DEFAULT_SAVED_SEARCH_LIMIT
): Promise<SavedSearch[]> {
  const { data, error } = await supabase
    .from('saved_searches')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) {
    console.error('Error fetching saved searches:', error)
    return []
  }

  return data || []
}

// VOY-411: normalize freeform location strings into a clean city name for
// downstream AI agents. Handles these shapes seen in the wild:
//   "Buenos Aires, Argentina (BUE)"   -> "Buenos Aires"
//   "Paris (CDG)"                     -> "Paris"
//   "paris"                           -> "Paris"
//   "New York, USA"                   -> "New York"
//   undefined / "" / null             -> undefined
export function normalizeCityName(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined
  // Strip trailing " (XXX)" airport code, split on first comma to drop country,
  // trim, then Title Case each word.
  const stripped = raw.replace(/\s*\([A-Z]{3}\).*$/, '').split(',')[0]?.trim()
  if (!stripped) return undefined
  return stripped
    .toLowerCase()
    .split(/\s+/)
    .map(w => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
}

// VOY-411: optional enrichment payload captured at save time so downstream
// AI agents (VOY-415/416) can read structured context instead of raw IATA codes.
export interface SavedSearchEnrichment {
  originCityName?: string
  destinationCityName?: string
  cabinClass?: string
  hotelStarMin?: number
  // VOY-411: max is `null` for an unbounded upper budget (user set min only).
  // Avoid sentinel numbers like Number.MAX_SAFE_INTEGER so downstream AI agents
  // can distinguish "no ceiling" from "9 quadrillion ceiling".
  budgetRange?: { min: number; max: number | null }
  resultsCount?: number
  lowestPriceSeen?: number
  searchContext?: string
  searchSource?: string
}

export async function saveSearch(
  userId: string,
  searchType: 'flight' | 'hotel' | 'trip',
  query: Record<string, any>,
  enrichment?: SavedSearchEnrichment
): Promise<void> {
  const { error } = await supabase
    .from('saved_searches')
    .insert({
      user_id: userId,
      search_type: searchType,
      query,
      origin_city_name: enrichment?.originCityName ?? null,
      destination_city_name: enrichment?.destinationCityName ?? null,
      cabin_class: enrichment?.cabinClass ?? null,
      hotel_star_min: enrichment?.hotelStarMin ?? null,
      budget_range: enrichment?.budgetRange ?? null,
      results_count: enrichment?.resultsCount ?? null,
      lowest_price_seen: enrichment?.lowestPriceSeen ?? null,
      search_context: enrichment?.searchContext ?? null,
      search_source: enrichment?.searchSource ?? 'search',
    })

  if (error) throw error
}

export async function deleteSavedSearch(id: string): Promise<void> {
  const { error } = await supabase
    .from('saved_searches')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// VOY-411: toggle `is_favorited` on a saved search. Favorited rows are
// exempt from the 50-per-user cap enforced by cleanup_saved_searches(),
// so this is how a user pins a search they want to keep. The UI toggle
// itself lands in VOY-412 (click-to-research) / VOY-417 (You page
// redesign); this helper exists now so those tickets have a writer.
export async function favoriteSearch(
  id: string,
  isFavorited: boolean
): Promise<void> {
  const { error } = await supabase
    .from('saved_searches')
    .update({ is_favorited: isFavorited })
    .eq('id', id)

  if (error) throw error
}